import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import Form, { FormRef } from "../../components/Form";
import Loading from "../../components/Loading/Loading";
import { FooterPaginas } from "../../components/FooterPaginas/FooterPaginas";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { credenciaisApi, ValidarTokenResponse } from "../../hooks/credenciaisApi";
import {
  ContainerCredenciais,
  ContainerLoading,
  FooterWrapper,
} from "./styles";

type TipoCredencial = 'bling' | 'tiny' | 'melhor_envio' | 'correios' | 'outros';

interface LabelsCredencial {
  usuario: string;
  senha: string;
  sistema: string;
}

const LABELS_POR_TIPO: Record<TipoCredencial, LabelsCredencial> = {
  bling: {
    usuario: "Usuário Bling",
    senha: "Senha Bling",
    sistema: "Bling ERP"
  },
  tiny: {
    usuario: "Usuário Tiny",
    senha: "Senha Tiny",
    sistema: "Tiny ERP"
  },
  melhor_envio: {
    usuario: "E-mail Melhor Envio",
    senha: "Senha Melhor Envio",
    sistema: "Melhor Envio"
  },
  correios: {
    usuario: "Usuário Correios",
    senha: "Senha Correios",
    sistema: "Correios"
  },
  outros: {
    usuario: "Usuário",
    senha: "Senha",
    sistema: "Sistema"
  }
};

interface MensagemErro {
  titulo: string;
  texto: string;
  icon: 'error' | 'warning' | 'info' | 'success';
}

const MENSAGENS_ERRO: Record<string, MensagemErro> = {
  token_nao_informado: {
    titulo: 'Link Inválido',
    texto: 'Verifique se copiou o link corretamente.',
    icon: 'error'
  },
  token_invalido: {
    titulo: 'Link Inválido',
    texto: 'Este link é inválido ou já foi utilizado. Solicite um novo link ao suporte.',
    icon: 'error'
  },
  token_empresa_diferente: {
    titulo: 'Acesso Negado',
    texto: 'Este link não pertence à sua conta.',
    icon: 'error'
  },
  credenciais_ja_enviadas: {
    titulo: 'Credenciais Já Enviadas!',
    texto: 'Suas credenciais já foram enviadas anteriormente. O suporte já pode acessá-las.',
    icon: 'success'
  },
  solicitacao_encerrada: {
    titulo: 'Solicitação Encerrada',
    texto: 'Esta solicitação foi encerrada pelo suporte.',
    icon: 'info'
  },
  link_expirado: {
    titulo: 'Link Expirado',
    texto: 'Este link expirou. Solicite um novo link ao suporte.',
    icon: 'warning'
  },
  usuario_nao_informado: {
    titulo: 'Campo Obrigatório',
    texto: 'Preencha o campo de usuário.',
    icon: 'error'
  },
  senha_nao_informada: {
    titulo: 'Campo Obrigatório',
    texto: 'Preencha o campo de senha.',
    icon: 'error'
  },
  erro_processamento: {
    titulo: 'Erro ao Processar',
    texto: 'Não foi possível processar sua solicitação. Tente novamente.',
    icon: 'error'
  },
  default: {
    titulo: 'Erro',
    texto: 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.',
    icon: 'error'
  }
};

const Credenciais: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const api = credenciaisApi();
  const formRef = useRef<FormRef>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenData, setTokenData] = useState<ValidarTokenResponse | null>(null);
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [formValid, setFormValid] = useState(false);

  const mostrarAlerta = (codigo: string | undefined, redirecionar: boolean = true) => {
    const mensagem = MENSAGENS_ERRO[codigo || 'default'] || MENSAGENS_ERRO.default;

    Swal.fire({
      icon: mensagem.icon,
      title: mensagem.titulo,
      text: mensagem.texto,
      confirmButtonText: 'Entendi',
      confirmButtonColor: theme.colors.secondary,
    }).then(() => {
      if (redirecionar) {
        navigate('/');
      }
    });
  };

  useEffect(() => {
    const validarToken = async () => {
      if (!token) {
        mostrarAlerta('token_nao_informado');
        setLoading(false);
        return;
      }

      const response = await api.validarToken(token);

      if (response.valido) {
        setTokenData(response);
      } else {
        mostrarAlerta(response.codigo);
      }

      setLoading(false);
    };

    validarToken();
  }, [token]);

  const handleSubmit = async () => {
    if (!token) return;

    setSubmitting(true);

    const response = await api.submeterCredenciais(token, usuario, senha);

    if (response.sucesso) {
      Swal.fire({
        icon: 'success',
        title: 'Credenciais Enviadas!',
        text: 'Suas credenciais foram enviadas com sucesso. O suporte agora pode acessá-las para realizar o procedimento solicitado.',
        confirmButtonText: 'Ok',
        confirmButtonColor: theme.colors.secondary,
      }).then(() => {
        navigate('/');
      });
    } else {
      // Erro de submissão - não redireciona para permitir nova tentativa
      mostrarAlerta(response.codigo, false);
    }

    setSubmitting(false);
  };

  const getLabels = (): LabelsCredencial => {
    if (!tokenData?.tipo) {
      return LABELS_POR_TIPO.outros;
    }

    const labels = LABELS_POR_TIPO[tokenData.tipo as TipoCredencial];

    // Se for "outros", usar o nome customizado se disponível
    if (tokenData.tipo === 'outros' && tokenData.tipo_outros) {
      return {
        ...labels,
        sistema: tokenData.tipo_outros
      };
    }

    return labels;
  };

  const labels = getLabels();

  // Estado de loading
  if (loading) {
    return (
      <ContainerLoading theme={theme}>
        <div className="loading-content">
          <Loading />
          <p className="loading-text fonteTexto">Validando solicitação...</p>
        </div>
      </ContainerLoading>
    );
  }

  // Se não tem tokenData válido, o alerta já foi mostrado e o usuário será redirecionado
  if (!tokenData) {
    return (
      <ContainerLoading theme={theme}>
        <div className="loading-content">
          <Loading />
        </div>
      </ContainerLoading>
    );
  }

  // Formulário de credenciais
  return (
    <ContainerCredenciais theme={theme}>
      <p className="descricao fonteTexto">
        O suporte solicitou suas credenciais de acesso ao {labels.sistema}.
        Preencha os campos abaixo para enviar de forma segura.
      </p>

      <div className="content">
        <div className="info-box">
          <div className="info-label fonteSubtitulo">Sistema</div>
          <div className="info-value fonteRotulos">{labels.sistema}</div>

          {tokenData?.motivo && (
            <div className="info-motivo">
              <div className="info-label fonteSubtitulo">Motivo da solicitação</div>
              <div className="info-value fonteRotulos">{tokenData.motivo}</div>
            </div>
          )}
        </div>

        <p className="aviso fonteTexto">
          Suas credenciais serão criptografadas e armazenadas de forma segura.
          Apenas o suporte poderá visualizá-las por tempo limitado.
        </p>

        <Form ref={formRef} id="credenciais-form" onSubmit={() => handleSubmit()} setFormValid={setFormValid}>
          <div className="form-group">
            <label className="label fonteRotulos">{labels.usuario}</label>
            <Input
              type="text"
              placeholder={`Digite seu ${labels.usuario.toLowerCase()}`}
              value={usuario}
              onChange={(newValue) => setUsuario(newValue)}
              required
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label className="label fonteRotulos">{labels.senha}</label>
            <Input
              type="password"
              placeholder="Digite sua senha"
              value={senha}
              onChange={(newValue) => setSenha(newValue)}
              required
              allowEye={true}
              autoComplete="new-password"
            />
          </div>
        </Form>
      </div>

      <FooterPaginas>
        <FooterWrapper theme={theme}>
          <Button
            title={submitting ? "Enviando..." : "Enviar Credenciais"}
            onClick={() => formRef.current?.submitForm()}
            loading={submitting}
            disabled={!formValid || submitting}
          />
        </FooterWrapper>
      </FooterPaginas>
    </ContainerCredenciais>
  );
};

export default Credenciais;
